import React, { useState, useEffect } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import { getStripeErrorMessage, logStripeError } from '../../utils/stripeErrors';

/**
 * Stripe Checkout Form Component
 *
 * Embedded payment form with Stripe PaymentElement.
 * Handles payment confirmation and error states.
 *
 * @param {Object} props
 * @param {number} props.orderId - Order ID for redirect URL
 * @param {number} props.amount - Amount to charge
 * @param {Function} [props.onSuccess] - Success callback
 * @param {Function} [props.onError] - Error callback
 */
export function CheckoutForm({ orderId, amount, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  // Payment Element ready handler
  const handlePaymentElementReady = () => {
    console.log('[CheckoutForm] Payment Element ready');
    setPaymentElementReady(true);
  };

  // Form submission handler
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Stripe.js hasn't loaded yet
    if (!stripe || !elements) {
      console.warn('[CheckoutForm] Stripe not ready yet');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    console.log('[CheckoutForm] Starting payment confirmation...');

    try {
      // Validate form before submission
      const { error: submitError } = await elements.submit();
      if (submitError) {
        logStripeError('Form submission validation', submitError);
        const userMessage = getStripeErrorMessage(submitError);
        setErrorMessage(userMessage);
        setIsProcessing(false);

        if (onError) {
          onError(submitError, userMessage);
        }
        return;
      }

      // Confirm payment
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success?orderId=${orderId}`,
        },
        // Prevent automatic redirect on success to handle it ourselves
        redirect: 'if_required',
      });

      if (confirmError) {
        // Payment failed - show error
        logStripeError('Payment confirmation', confirmError);
        const userMessage = getStripeErrorMessage(confirmError);
        setErrorMessage(userMessage);
        setIsProcessing(false);

        if (onError) {
          onError(confirmError, userMessage);
        }
      } else {
        // Payment succeeded without redirect (rare, usually redirects for 3D Secure)
        console.log('[CheckoutForm] Payment confirmed successfully');
        setIsProcessing(false);

        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error('[CheckoutForm] Unexpected error during payment:', error);
      const errorMsg = 'An unexpected error occurred. Please try again.';
      setErrorMessage(errorMsg);
      setIsProcessing(false);

      if (onError) {
        onError(error, errorMsg);
      }
    }
  };

  // Timeout handler - show message if payment takes too long
  useEffect(() => {
    if (!isProcessing) return;

    const timeout = setTimeout(() => {
      if (isProcessing) {
        const timeoutMsg =
          'Payment is taking longer than expected. Please check your order status or contact support.';
        console.warn('[CheckoutForm] Payment timeout');
        setErrorMessage(timeoutMsg);
        setIsProcessing(false);

        if (onError) {
          onError(new Error('timeout'), timeoutMsg);
        }
      }
    }, 60000); // 60 second timeout

    return () => clearTimeout(timeout);
  }, [isProcessing, onError]);

  return (
    <form onSubmit={handleSubmit} className="stripe-checkout-form">
      {/* Amount Display */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-gray-700">
            Amount to pay:
          </span>
          <span className="text-2xl font-bold text-gray-900">
            £{parseFloat(amount).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Payment Element */}
      <div className="mb-6">
        <PaymentElement
          onReady={handlePaymentElementReady}
          options={{
            layout: {
              type: 'accordion',
              defaultCollapsed: false,
              radios: false,
              spacedAccordionItems: true,
            },
            fields: {
              billingDetails: {
                address: {
                  country: 'auto',
                },
              },
            },
          }}
        />
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h4 className="font-semibold text-red-800 mb-1">Payment Error</h4>
              <p className="text-red-700 text-sm">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || !paymentElementReady || isProcessing}
        className={`
          w-full py-4 px-6 rounded-lg font-semibold text-lg
          transition-all duration-200 flex items-center justify-center
          ${
            !stripe || !paymentElementReady || isProcessing
              ? 'bg-gray-300 text-gray-700 cursor-not-allowed'
              : 'bg-white text-gray-900 border-2 border-gray-900 hover:bg-gray-900 hover:text-white shadow-sm hover:shadow-md'
          }
        `}
      >
        {isProcessing ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Processing Payment...
          </>
        ) : (
          'Pay Now'
        )}
      </button>

      {/* Security Badge */}
      <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
        Secure payment powered by Stripe
      </div>

      {/* Additional Help Text */}
      {!paymentElementReady && stripe && (
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">Loading payment form...</p>
        </div>
      )}
    </form>
  );
}

CheckoutForm.propTypes = {
  orderId: PropTypes.number.isRequired,
  amount: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

export default CheckoutForm;
